import React, { useState } from "react";
import {
  Box, Typography, Alert, CircularProgress, TextField, Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import ThemeToggle from "./ThemeToggle";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const BRAND = {
  navy: "#0a0e27",
  navyDark: "#0f1233",
  accent: "#637cdb",
  accentDark: "#3F6AC0",
};

// Same label-above-input wrapper used by Login.js/Register.js.
function Field({ label, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase", mb: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// Shared login shell for the standalone staff dashboards (Logistics,
// Telehealth Clinician Review) — each is its own login surface, own
// localStorage key, own dashboard route, per the "three separate standalone
// dashboards" requirement. Both hit the same /staff-login endpoint, which
// enforces the role server-side. Visual treatment (glass card, blurred
// background blobs, motion entrance) matches the main Login.js so staff
// entry points don't feel like a different, unfinished product.
export default function StaffLogin({ role, storageKey, dashboardPath, badge, title }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.68)";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 15,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "2px" },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/staff-login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (res.data.role !== role) {
        setLoading(false);
        setError("This account doesn't have access to this dashboard.");
        return;
      }
      localStorage.setItem(storageKey, JSON.stringify({
        email: res.data.email,
        name: res.data.name,
        token: res.data.token,
        forcePasswordChange: res.data.force_password_change,
      }));
      navigate(dashboardPath, { replace: true });
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.detail || "Login failed. Please try again.");
    }
  };

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: bg, display: "flex", flexDirection: "column",
      overflow: "hidden", position: "relative",
    }}>
      {/* Animated background blobs — same treatment as the main Login page */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-10%", left: "-5%", size: 460, color: isDark ? "rgba(10,14,39,0.18)" : "rgba(10,14,39,0.07)", delay: 0 },
          { top: "55%", right: "-8%", size: 380, color: isDark ? "rgba(99,124,219,0.08)" : "rgba(99,124,219,0.12)", delay: 1.5 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(80px)",
          }}
            animate={{ scale: [1, 1.12, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 8 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      <Box sx={{
        position: "relative", zIndex: 10,
        bgcolor: isDark ? "rgba(2,6,15,0.88)" : "rgba(10,14,39,0.97)",
        backdropFilter: "blur(16px)", borderBottom: "2px solid rgba(99,124,219,0.3)",
      }}>
        <Box sx={{
          maxWidth: 1200, mx: "auto", px: 3, height: 64,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
          </Box>
          <ThemeToggle />
        </Box>
      </Box>

      <Box sx={{ position: "relative", zIndex: 10, px: 3, pt: 2 }}>
        <Box component={Link} to="/" sx={{
          display: "flex", alignItems: "center", gap: 0.75, textDecoration: "none", width: "fit-content",
          fontSize: 13, fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b",
          "&:hover": { color: BRAND.accent },
        }}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to Sign In
        </Box>
      </Box>

      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 4,
        position: "relative", zIndex: 10,
      }}>
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 400 }}
        >
          <Box sx={{
            borderRadius: "20px", overflow: "hidden",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.1)"}`,
            boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(10,14,39,0.12)",
            bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
          }}>
            <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.accent})` }} />
            <Box sx={{ bgcolor: BRAND.navy, px: 4, py: 3 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", color: BRAND.accent, mb: 1 }}>
                {badge}
              </Typography>
              <Typography sx={{ fontFamily: "'Michroma', sans-serif", fontSize: 18, fontWeight: 400, color: "#fff" }}>
                {title}
              </Typography>
            </Box>

            <Box sx={{ px: 4, py: 4 }}>
              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>
                    {error}
                  </Alert>
                </motion.div>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Field label="Email address" isDark={isDark}>
                  <TextField hiddenLabel type="email" fullWidth required
                    placeholder="you@proove.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    sx={inputSx}
                  />
                </Field>
                <Field label="Password" isDark={isDark}>
                  <TextField hiddenLabel type="password" fullWidth required
                    placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    sx={inputSx}
                  />
                </Field>
                <Button type="submit" fullWidth variant="contained" disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.accentDark} 100%)`,
                    borderRadius: "10px", py: 1.4, fontWeight: 700, textTransform: "none", fontSize: 16,
                    boxShadow: "0 8px 24px rgba(10,14,39,0.25)",
                    "&:hover": { bgcolor: BRAND.navyDark },
                  }}>
                  {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
